import type { VideoAvatarMode } from '@mfc/mfc-video-sdk'
import { cloneDeep, isEqualWith, merge } from 'lodash-es'
import {
    GLOBAL_VIDEO_CACHE,
    INSTANCE_COUNTER,
    SECURESTATE,
    USERSSTATE,
    VIDEOSTATE,
} from '.'
import {
    VideoDimensions,
    VideoEventHandler,
    VideoInitOptions,
    VideoPlaceholder,
    VideoStateInstance,
    VideoTouchListeners,
} from '../../types/state'
import { PAPER_HEIGHTS } from '../components/theme'
import {
    FloaterSize,
    SnapSize,
    UserEvent,
    VideoEvent,
    VideoType,
} from '../constants/enums'
import {
    VIDEO_AVATARS_OPTIONS,
    VIDEO_BOTTOM_ROUNDED_CONTAINER_CLASSES,
    VIDEO_CONTAINER_CLASSES,
    VIDEO_CONTAINER_PREFIX_ID,
    VIDEO_ELEVATED_CONTAINER_CLASSES,
    VIDEO_FULL_ROUNDED_CONTAINER_CLASSES,
    VIDEO_PLAYER_ID,
    VIDEO_ZOOMED_CONTAINER_CLASSES,
} from '../constants/video'
import { showsAgeGatedPoster } from '../mfcLogic/ageGatedVideo'
import {
    generateVideoConfig,
    getZoomConfig,
    type VideoConfig,
} from '../mfcLogic/generateVideoConfig'
import { FCVIDEO_OFFLINE } from '../mfcLogic/mfcConstants'
import {
    acrHidesLiveControls,
    getVideoPlayerClass,
    videoPlayerReady,
} from '../mfcLogic/videoPlayerReady'
import { areFunctionsEqual } from '../utilities/comparison'
import { parseStyles, stylesToString } from '../utilities/css'
import { aspectRatio } from '../utilities/dimensions'
import { floatEqual } from '../utilities/math'
import { safeObj } from '../utilities/safe'
import { getVideoState, resolveID } from './getState'
import { slugToID } from './internal/users'
import {
    applyViewModeButtons,
    videoViewModeHandlers,
} from './internal/videoViewModeControls'
import { pubSubUsers, pubSubVideo } from './pubSub'
import { registerSnaps, registerTimer } from './state.timers'

const DEACTIVATE_TIMEOUT = 1500

export const videoElement = (id: number) =>
    document.getElementById(
        VIDEO_PLAYER_ID.replace('{{ID}}', String(id)),
    ) as HTMLVideoElement

const defaultState: VideoStateInstance = {
    cache: {
        dimensions: { height: 0, width: 0 },
        id: null,
        hidden: false,
        options: null,
        disableZoom: null,
        portraitTheater: false,
        playing: null,
        onDimensions: null,
        onPlaying: null,
        state: null,
        sessionId: null,
    },
    onclick: undefined,
    wasAutoMuted: null,
    initOptions: null,
    unsubscribe: null,
    placeholders: [],
    container: null,
    containerClasses: [],
    autoMutedBy: {},
    instance: null,
    playing: null,
    videoAvatar: null,
    muted: null,
    autoMuted: {},
    volume: null,
    config: null,
    new: true,
    portraitTheater: false,
    id: null,
    timers: {
        callback: null,
        retryUser: null,
        retrySession: null,
        retryVideoPlayer: null,
        reset: null,
        hide: null,
        raf: null,
    },
}

const emitInitializing = (
    id: number,
    videoType: VideoType = VideoType.Room,
) => {
    pubSubVideo.emit(VideoEvent.INITIALIZING + '::' + videoType, id, true)
}

const emitPlaying = (id: number, playing: boolean | null) => {
    pubSubVideo.emit(VideoEvent.PLAYING + '::' + id, playing, true)
}

const setVideoAvatar = (
    state: VideoStateInstance,
    id: number,
    mode: VideoAvatarMode | null,
) => {
    if (state.videoAvatar === mode) return
    state.videoAvatar = mode
    pubSubVideo.emit(VideoEvent.AVATAR + '::' + id, mode, true)
}

// useVideoInstance: the model's player was replaced (rendered) or torn down
const emitInstance = (id: number) => {
    pubSubVideo.emit(VideoEvent.INSTANCE + '::' + id, undefined, true)
}

const emitDimensions = (videoType: VideoType, dimensions: VideoDimensions) => {
    pubSubVideo.emit(VideoEvent.DIMENSIONS + '::' + videoType, dimensions, true)
}

// A host that drags the player (addVideoTouchListeners) owns its touches and
// re-dispatches a tap as a click, so the <video>, which hears a touch before
// the drag on its container does, takes the host's onclick on click alone
const bindOnclick = (id: number) => {
    const state = VIDEOSTATE.videos[id]
    const video = videoElement(id)
    if (!state || !video) return
    const { onclick, touchListeners } = state
    video.ontouchstart = touchListeners ? null : onclick
    video.ontouchend = touchListeners ? null : onclick
    video.onclick = onclick
}

const setOnclick = (onclick: VideoEventHandler, id: number) => {
    const state = VIDEOSTATE.videos[id]

    if (state.onclick === onclick) {
        return
    }

    const video = videoElement(id)
    // console.log(`[video ${id}] video element`, video)
    // console.log(`[video ${id}] video instance`, state.instance)
    if (!video) {
        return
    }
    state.onclick = onclick
    bindOnclick(id)
}

/*
    Mutes the player for the app without touching the global mute, and keeps
    it on the state so a rebuild starts the new player the same way. A hidden
    or auto-muted player ignores its own volume change (see onVolumeChange),
    so without this a rebuild builds it from the global mute and it comes
    back with sound. The player's own `muted` cannot carry this: the SDK also
    reports the mute it applies when the browser blocks autoplay with sound.
*/
const setInstanceMute = (state: VideoStateInstance, muted: boolean) => {
    state.hostMuted = muted
    state.instance?.setMute(muted)
}

export const setVideoMute = (id: number, muted: boolean) => {
    const state = VIDEOSTATE.videos[id]
    if (state) setInstanceMute(state, muted)
}

const reset = (id: number) => {
    // console.log(`[video.reset ${id}]`)

    const state = VIDEOSTATE.videos[id]

    cancelAnimationFrame(state.timers.retryUser)
    cancelAnimationFrame(state.timers.retrySession)
    cancelAnimationFrame(state.timers.retryVideoPlayer)

    // console.log(`[video.reset ${id}] callback timer`, state.timers.callback)

    clearTimeout(state.timers.callback)
    clearTimeout(state.timers.reset)
    clearTimeout(state.timers.hide)

    state.timers.callback = null
    state.timers.retryUser = null
    state.timers.retrySession = null
    state.timers.retryVideoPlayer = null
    state.timers.reset = null
    state.timers.hide = null

    if (state.unsubscribe) {
        state.unsubscribe()
    }

    if (state.instance) {
        state.instance.Disconnect()
    }

    if (state.container) {
        state.container.remove()
    }

    if (state.cache.playing) {
        state.cache.playing = null
        emitPlaying(id, null)
        state.placeholders.forEach(({ onPlaying }) => {
            onPlaying?.(null)
        })
    }

    setVideoAvatar(state, id, null)

    // console.log(`[video.reset ${id}] deleting state`)
    delete VIDEOSTATE.videos[id]
    emitInstance(id)
}

const hide = (id: number) => {
    // console.log(`[video ${id}] attempting to hide`)

    const state = VIDEOSTATE.videos[id]

    const hideState = (target: VideoStateInstance) => {
        clearTimeout(target.timers.hide)
        target.timers.hide = null
        setInstanceMute(target, true)
        target.cache.hidden = true
    }

    const execute = () => {
        // console.log(`[video ${id}] temporarily hiding`)
        hideState(state)
        // a transition rebuild during the delay replaced the state, and the
        // replacement plays hidden too
        const current = VIDEOSTATE.videos[id]
        if (current && current !== state) hideState(current)
        if (current) transform({ left: 0, top: 0, height: 0, width: 0 }, id)
    }

    clearTimeout(state.timers.hide)
    state.timers.hide = setTimeout(execute, 250)
}

const runAnimationFrame = (id: number) => () => {
    const state = VIDEOSTATE.videos[id]

    cancelAnimationFrame(state.timers.raf)
    state.timers.raf = requestAnimationFrame(runAnimationFrame(id))

    const placeholder = state.placeholders[state.placeholders.length - 1]
    if (!placeholder) {
        return
    }

    const { node, options, onPlaying, onDimensions, videoType } = placeholder
    if (!node) {
        return
    }

    clearTimeout(state.timers.hide)
    state.timers.hide = null

    if (state.cache.hidden && state.instance) {
        // console.log(`[video ${id}] unhiding`)
        const { mute } = getVideoState()

        // console.log(`[video ${id}] setting mute state (hidden video)`)

        setInstanceMute(
            state,
            state.initOptions?.mute !== undefined
                ? state.initOptions?.mute
                : mute,
        )

        state.cache.hidden = false
    }

    const nodeRect = node.getBoundingClientRect()
    const {
        height: nodeHeight,
        width: nodeWidth,
        left: nodeLeft,
        top: nodeTop,
    } = nodeRect

    const {
        height: videoHeight,
        width: videoWidth,
        left: videoLeft,
        top: videoTop,
    } = state.container.getBoundingClientRect()

    const {
        onlyVolumeControls = false,
        allowControls = true,
        disableZoom = false,
        portraitTheater = false,
        forceHeight,
        onclick,
    } = safeObj(options)

    // Taken from the top placeholder before sizing, so a second placeholder
    // for the model (the menu over a zoomed room) never gets the room's box;
    // the SDK mode follows in the options block
    state.portraitTheater = portraitTheater

    const { height: calcHeight, width: calcWidth } = aspectRatio(nodeRect)
    // the SDK's portrait theater mode cover-zooms the stream to the whole
    // box, so the container takes the placeholder as-is instead of the
    // largest 16:9 box that fits inside it
    const fillPlaceholder = state.portraitTheater
    const width = fillPlaceholder || forceHeight ? nodeWidth : calcWidth
    const height = fillPlaceholder
        ? nodeHeight
        : !forceHeight
          ? calcHeight
          : (nodeWidth * 9) / 16
    const left = (nodeWidth - width) / 2 + nodeLeft
    const top = nodeHeight > 0 ? (nodeHeight - height) / 2 + nodeTop : nodeTop

    if (forceHeight && !floatEqual(nodeHeight, height)) {
        node.style.height = `${height}px`
    }

    if (!GLOBAL_VIDEO_CACHE[videoType]) {
        GLOBAL_VIDEO_CACHE[videoType] = {
            dimensions: { height, width },
        }
        emitDimensions(videoType, { height, width })
    } else {
        const globalCache = GLOBAL_VIDEO_CACHE[videoType]
        if (!globalCache.dimensions) {
            globalCache.dimensions = { height, width }
            emitDimensions(videoType, { height, width })
        } else {
            const globalCacheDimensionsEqual =
                floatEqual(globalCache.dimensions.height, height) &&
                floatEqual(globalCache.dimensions.width, width)
            if (!globalCacheDimensionsEqual) {
                globalCache.dimensions = { height, width }
                emitDimensions(videoType, { height, width })
            }
        }
    }

    const cacheDimensionsEqual =
        floatEqual(state.cache.dimensions.height, height) &&
        floatEqual(state.cache.dimensions.width, width)

    if (!cacheDimensionsEqual || state.cache.onDimensions !== onDimensions) {
        state.cache.dimensions = { height, width }
        state.cache.onDimensions = onDimensions
        onDimensions?.({ height, width })
    }

    if (!state.instance) {
        if (state.cache.playing) {
            state.cache.playing = null
            emitPlaying(id, null)
            onPlaying?.(null)
        }
        return
    }

    if (
        state.cache.playing !== state.playing ||
        state.cache.onPlaying !== onPlaying
    ) {
        if (state.cache.playing !== state.playing) {
            state.cache.playing = state.playing
            emitPlaying(id, state.playing)
        }
        onPlaying?.(state.playing)
        state.cache.onPlaying = onPlaying
    }

    if (
        state.cache.options === options &&
        floatEqual(videoHeight, height) &&
        floatEqual(videoWidth, width) &&
        floatEqual(videoLeft, left) &&
        floatEqual(videoTop, top)
    ) {
        return
    }

    if (state.cache.options !== options) {
        setOnclick(onclick, id)
        // A placeholder may take controls away from an age-gated visitor's
        // player but not hand the live feed back the full bar, Theater and
        // Full Window included (acrHidesLiveControls). The SDK clamps
        // setOnlyVolumeControls to its ACR mode itself.
        state.instance.setAllowControls(
            allowControls && (!acrHidesLiveControls() || !!state.videoAvatar),
        )
        state.instance.setOnlyVolumeControls(onlyVolumeControls)
        applyZoomOptions(state, disableZoom, portraitTheater)
        applyViewModeButtons(state.instance, options, portraitTheater)
        state.cache.options = options
    }

    // the rects are viewport-relative but the container is positioned in the
    // document, which iOS scrolls to lift a focused input above the keyboard
    transform(
        {
            left: left + window.scrollX,
            top: top + window.scrollY,
            height,
            width,
            ...options,
        },
        id,
    )
}

export const activateTransform = (id: number) => {
    const state = VIDEOSTATE.videos[id]
    if (state) {
        cancelAnimationFrame(state.timers.raf)
        state.timers.raf = requestAnimationFrame(runAnimationFrame(id))
    }
}

export const deactivateTransform = (id: number) => {
    const state = VIDEOSTATE.videos[id]
    if (state) {
        cancelAnimationFrame(state.timers.raf)
        state.timers.raf = null
    }
}

const destroy = (id: number) => {
    if (!VIDEOSTATE.videos[id]) return
    // console.log(`[video ${id}] destroy`)
    deactivateTransform(id)
    reset(id)
}

/*
    Layouts set the container's app classes (enableRoundedVideo) and touch
    listeners (addVideoTouchListeners) from effects that re-run only on their
    own deps. A transition rebuild in resolveInstance swaps the container and
    changes none of those deps, so the state keeps what the layout last asked
    for and the rebuild re-applies it to the replacement. Without that the
    replacement loses `elevated` and sits under the room, and Portrait's
    drag-to-dismiss goes dead. The playing channel cannot re-run the layouts
    either: in a hidden tab no animation frame runs, so cache.playing stays
    true across the rebuild.

    The outgoing container keeps its listeners until it is torn down: a
    touch keeps its first target, so a drag that started before the rebuild
    ends on the outgoing container. Dropping them there would leave the room
    half dragged and the transform deactivated, so the replacement would
    never be positioned. Removing the model's listeners clears both.
*/
const attachTouchListeners = (state: VideoStateInstance) => {
    const { container, touchListeners } = state
    if (!container || !touchListeners) return
    const { onTouchCancel, onTouchEnd, onTouchMove, onTouchStart } =
        touchListeners
    container.addEventListener('touchstart', onTouchStart)
    container.addEventListener('touchmove', onTouchMove)
    container.addEventListener('touchend', onTouchEnd)
    container.addEventListener('touchcancel', onTouchCancel)
    state.removeTouch = () => {
        container.removeEventListener('touchstart', onTouchStart)
        container.removeEventListener('touchmove', onTouchMove)
        container.removeEventListener('touchend', onTouchEnd)
        container.removeEventListener('touchcancel', onTouchCancel)
    }
}

export const addVideoTouchListeners = (
    id: number,
    listeners: VideoTouchListeners,
) => {
    removeVideoTouchListeners(id)
    const state = VIDEOSTATE.videos[id]
    if (!state) return
    state.touchListeners = listeners
    attachTouchListeners(state)
    bindOnclick(id)
}
export const removeVideoTouchListeners = (id: number) => {
    Object.entries(VIDEOSTATE.videos).forEach(([key, state]) => {
        // the current container and any outgoing one ('transitioning_N_id')
        const outgoing =
            key.startsWith('transitioning_') && key.endsWith('_' + id)
        if (key !== String(id) && !outgoing) return
        state.removeTouch?.()
        state.removeTouch = undefined
        state.touchListeners = undefined
    })
    bindOnclick(id)
}

// Shows the control bar and re-arms its inactivity timer like a pointer
// move. A no-op while the player is not rendered, destroyed, or has controls
// disabled (SDK 0.1.1).
export const showVideoControls = (id: number) => {
    VIDEOSTATE.videos[id]?.instance?.notifyUserActivity()
}

/*
    Zoom and the Portrait layout's zoomed level (the SDK's portrait theater
    mode) both come from the top placeholder and go to the SDK together, in
    this order, because the mode snapshots the zoom config on entry, forces
    the gestures on while it lasts and restores the snapshot on exit:
    - enter from the layout's base config, never from `false`: the SDK
      snapshots `false` and restores it on exit, but while the mode lasts it
      resolves `false` to its own defaults, which bring the Scroll wheel zoom
      toggle back on phones;
    - while the mode is on, a zoom policy change is only recorded;
    - on exit, leave the mode first, then put the policy back.
    Only on a real change: setZoomOptions rebuilds the SDK's gesture handler
    (dropping a pinch or pan in flight) and redraws an open Settings menu,
    and a new options object arrives on every re-register.
*/
const applyZoomOptions = (
    state: VideoStateInstance,
    disableZoom: boolean,
    portraitTheater: boolean,
) => {
    const { instance, cache } = state
    const wasPortraitTheater = cache.portraitTheater
    // `?? getZoomConfig()`: a missing zoom would resolve to the SDK's
    // defaults, which turn both gestures on
    const baseZoom = state.config?.zoom ?? getZoomConfig()

    if (portraitTheater && !wasPortraitTheater) {
        if (cache.disableZoom) instance.setZoomOptions(baseZoom)
        instance.setPortraitTheaterMode(true)
    } else if (!portraitTheater && wasPortraitTheater) {
        instance.setPortraitTheaterMode(false)
        if (disableZoom) instance.setZoomOptions(false)
    } else if (!portraitTheater && cache.disableZoom !== disableZoom) {
        instance.setZoomOptions(disableZoom ? false : baseZoom)
    }
    cache.disableZoom = disableZoom
    cache.portraitTheater = portraitTheater
}

const setupDependencies = (id: number) => {
    const existingVideoState = VIDEOSTATE.videos[id]
    if (existingVideoState) {
        clearTimeout(existingVideoState.timers.reset)
        existingVideoState.timers.reset = null
        return existingVideoState
    }

    const container = document.createElement('div')
    container.className = VIDEO_CONTAINER_CLASSES
    container.id = VIDEO_CONTAINER_PREFIX_ID + id
    document.body.appendChild(container)

    const state = cloneDeep(defaultState)
    state.container = container
    VIDEOSTATE.videos[id] = state
    return state
}

const toClassNames = (classes: string) => classes.trim().split(/\s+/)

const isClipAudible = (container: HTMLDivElement | null) => {
    const clip = container?.querySelector<HTMLVideoElement>(
        'video.mfcvs-video-avatar-clip',
    )
    return !!clip && !clip.muted && clip.volume > 0
}

/**
 * What auto-mute does to another player. A showing clip's bar mute is not
 * reported through onVolumeChange (SDK setMediaTarget), so `muted` describes
 * the live element, not the clip that is the only sound: a showing clip is
 * always muted, and restored on unregister only when the clip element
 * itself is audible. If the SDK renames that element nothing is found, so
 * the clip is muted and never auto-restored, the quiet side.
 */
export const autoMuteAction = (
    other: Pick<VideoStateInstance, 'muted' | 'videoAvatar' | 'container'>,
): 'skip' | 'mute' | 'muteAndRestore' => {
    if (other.videoAvatar) {
        return isClipAudible(other.container) ? 'muteAndRestore' : 'mute'
    }
    return other.muted ? 'skip' : 'muteAndRestore'
}

/**
 * Whether a rebuild of the same show type replaces the previous player in
 * place rather than handing over. The hand-over keeps the old instance
 * until the new one reports playing, which a clip never does, so an
 * instance that shows or will show a clip is replaced in place; even
 * handing over to a live one strands its clip when the room closes or the
 * model drops again before the stream plays. The instance kind covers an
 * offline-built player before its clip paints; the offline placard covers
 * a live-built one whose stream failed (the SDK sets it before the clip
 * mounts). Disconnect() then commits a terminal snapshot with videoAvatar
 * null, which the playbackstatechange handler applies.
 */
export const replacesInPlace = (
    previous: Pick<VideoStateInstance, 'config' | 'videoAvatar' | 'instance'>,
) =>
    previous.config?.modelState === FCVIDEO_OFFLINE ||
    !!previous.videoAvatar ||
    previous.instance?.getPlaybackState().placard === 'offline'

const APP_CONTAINER_STATE_CLASSES = [
    VIDEO_FULL_ROUNDED_CONTAINER_CLASSES,
    VIDEO_BOTTOM_ROUNDED_CONTAINER_CLASSES,
    VIDEO_ZOOMED_CONTAINER_CLASSES,
    VIDEO_ELEVATED_CONTAINER_CLASSES,
].flatMap(toClassNames)

// Toggle only the app-owned classes. The SDK keeps `mfcvs` and its state
// classes (mfcvs-paused, mfcvs-user-active, ...) on this same element and
// scopes every player style rule under `.mfcvs`, so a wholesale `className =`
// write silently unstyles the whole player.
const applyContainerClasses = (state: VideoStateInstance) => {
    const { classList } = state.container
    classList.remove(...APP_CONTAINER_STATE_CLASSES)
    classList.add(
        ...toClassNames(VIDEO_CONTAINER_CLASSES),
        ...state.containerClasses,
    )
}

export const enableRoundedVideo = (
    id: number,
    rounded = true,
    elevated = false,
    size = FloaterSize.Small,
    zoomVideo = false,
) => {
    const existingVideoState = VIDEOSTATE.videos[id]
    if (!existingVideoState?.container) return
    existingVideoState.containerClasses = [
        ...(rounded
            ? toClassNames(
                  size > FloaterSize.Small
                      ? VIDEO_BOTTOM_ROUNDED_CONTAINER_CLASSES
                      : VIDEO_FULL_ROUNDED_CONTAINER_CLASSES,
              )
            : []),
        ...(zoomVideo ? toClassNames(VIDEO_ZOOMED_CONTAINER_CLASSES) : []),
        ...(elevated ? toClassNames(VIDEO_ELEVATED_CONTAINER_CLASSES) : []),
    ]
    applyContainerClasses(existingVideoState)
}

export const register = (placeholder: VideoPlaceholder) => {
    const { options, id } = placeholder
    const { autoMute, onlyVolumeControls, allowControls } = options
    const hasExistingState = !!VIDEOSTATE.videos[id]

    // console.log(`[video ${id}] register`, { placeholder, hasExistingState })
    // removeVideoTouchListeners(id)

    //
    // WHY IS VIDEOSTATE UNDEFINED???
    //
    // 1. Direct load of a room (SSR), where Video mounts and calls `register`
    // before Room invokes `openRoom` (where `initVideo` is called).
    //
    // 2. If the user's internet is slow enough and the user is navigating
    // to a page that has yet to be lazy loaded, `initVideo` might have
    // already timed out.
    //
    // So we start the video again without "user" gesture, so it will be
    // muted with an overlay just like SSR, but that's better than leaving
    // the user with just snapshots.
    //
    const state = hasExistingState
        ? setupDependencies(id)
        : initVideo(id, {
              onlyVolumeControls,
              autoStarted: true,
              allowControls,
          })

    // const state = setupDependencies(id)

    state.placeholders.push(placeholder)

    if (autoMute) {
        // console.log(`[video ${id}] register: attempting to automute`)

        Object.values(VIDEOSTATE.videos).forEach((otherState) => {
            if (otherState.id === id || !otherState.instance) {
                return
            }
            const action = autoMuteAction(otherState)
            if (action === 'skip') {
                return
            }

            if (action === 'muteAndRestore') {
                state.autoMuted[otherState.id] = true
                state.autoMutedBy[id] = true
                otherState.wasAutoMuted = true
            }

            // console.log(`[video ${id}] register: setting mute state`)

            setInstanceMute(otherState, true)
        })

        // console.log(
        //     `[video ${id}] register: automuted`,
        //     JSON.stringify(state.autoMuted, null, 4),
        // )
    }

    if (state.placeholders.length > 1) {
        return
    }

    activateTransform(id)
}

export const unregister = (targetNode: HTMLElement, id: number) => {
    const state = VIDEOSTATE.videos[id]
    if (!state) return
    const autoMuted = Object.keys(state.autoMuted)

    // console.log(
    //     `[video ${id}] unregister: automuted`,
    //     JSON.stringify(autoMuted, null, 4),
    // )

    autoMuted.forEach((otherID) => {
        const otherState = VIDEOSTATE.videos[otherID]

        if (!otherState) {
            return
        }

        delete state.autoMuted[otherID]
        delete otherState.autoMutedBy[id]
        otherState.wasAutoMuted = false

        // console.log(
        //     `[video ${id}] unregister: attempting to set mute state of ${otherID} "false". Hidden: ${otherState.cache.hidden}`,
        // )

        if (!otherState.cache.hidden) {
            // console.log(
            //     `[video ${id}] unregister: setting mute state of ${otherID} "false"`,
            // )
            setInstanceMute(otherState, false)
        }
    })

    state.cache.options = null
    state.placeholders = state.placeholders.filter(
        ({ node }) => node !== targetNode,
    )

    if (state.placeholders.length > 0) {
        // don't destroy the video if there are other video instances playing for this id
        return
    }

    hide(id)
    deactivateTransform(id)
}

// the players muted for a clip the app plays outside them, to restore after
const mutedForClip = new Set<number>()

/**
 * Mutes the players for a clip playing with sound over them (the video
 * avatar viewer), as a registering autoMute player mutes the others: the
 * flag keeps the mute from reading as the viewer's own, which would change
 * their mute everywhere.
 */
export const muteVideosForClip = () => {
    Object.values(VIDEOSTATE.videos).forEach((state) => {
        if (!state.instance || mutedForClip.has(state.id)) return
        const action = autoMuteAction(state)
        if (action === 'skip') return
        if (action === 'muteAndRestore') {
            mutedForClip.add(state.id)
            state.wasAutoMuted = true
        }
        setInstanceMute(state, true)
    })
}

/** Gives back the sound muteVideosForClip took, as unregister does */
export const restoreVideosAfterClip = () => {
    mutedForClip.forEach((id) => {
        const state = VIDEOSTATE.videos[id]
        if (!state) return
        state.wasAutoMuted = false
        if (!state.cache.hidden) setInstanceMute(state, false)
    })
    mutedForClip.clear()
}

const destroyStaleEntries = () => {
    Object.entries(VIDEOSTATE.videos).forEach(([idStr, state]) => {
        if (idStr.startsWith('transitioning_')) {
            // console.log(`[video ${idStr}] found transitioning instance`)

            const [, , id] = idStr.split('_')
            const newState = VIDEOSTATE.videos[id]

            if (newState && !newState?.playing && state.new) {
                //
                // ensure state remains untouched for at least the duration of
                // one round of "DEACTIVATE_TIMEOUT"
                //
                state.new = false
                // console.log(`[video ${idStr}] not ready to transition yet`)
                return
            }

            destroyTransitionInstance(state, idStr)

            return
        }

        if (state.placeholders.length > 0) {
            return
        }

        if (state.new) {
            //
            // ensure state remains untouched for at least the duration of
            // one round of "DEACTIVATE_TIMEOUT"
            //
            state.new = false
            return
        }

        const id = Number(idStr)

        clearTimeout(state.timers.reset)
        state.timers.reset = setTimeout(() => {
            clearTimeout(state.timers.reset)
            state.timers.reset = null
            // console.log(`[video ${id}] resetting from timeout`)
            if (state.placeholders.length > 0) {
                // console.log(`[video ${id}] resetting from timeout no more`)
                return
            }

            // console.log(`[video ${id}] no placeholders, resetting from timeout`)
            destroy(id)
        }, DEACTIVATE_TIMEOUT)
    })
}

if (typeof window !== 'undefined') {
    registerTimer('videoCleanup', destroyStaleEntries, DEACTIVATE_TIMEOUT * 2)
}

interface VideoTransformConfig {
    forefront?: boolean
    pinToTop?: boolean
    styles?: string
    height: number
    width: number
    left: number
    top: number
}

// TODO: maybe we should just call this directly on room size updates???
// all the situations where the video size/location should be adjusted are
// handled, so it should be possible to call transform there so the video size/location
// would be updated more quickly (no rendering in wrong spot for a frame)

export const transform = (config: VideoTransformConfig, id: number) => {
    const {
        forefront,
        pinToTop,
        styles,
        height = 0,
        width = 0,
        left = 0,
        top: y = 0,
    } = config
    const top = pinToTop
        ? window?.visualViewport?.pageTop || window?.scrollY
        : y
    const zIndex = PAPER_HEIGHTS.videoForefront

    const style =
        `height:${height || 0}px;` +
        `width:${width || 0}px;` +
        `left:${left}px;` +
        `top:${top}px;` +
        (forefront ? `z-index:${zIndex};` : '') +
        stylesToString(parseStyles(styles))

    const state = VIDEOSTATE.videos[id]

    state.container.setAttribute('style', style)
}

const destroyTransitionInstance = (state, id) => {
    // console.log(`[video ${id}] destroying previous instance`)

    if (state.instance) {
        state.instance.Disconnect()
        state.instance = null
    }

    if (state.container) {
        state.container.remove()
        state.container = null
    }

    delete VIDEOSTATE.videos[id]
}

export const initVideo = (
    rawSlugOrId: string | number,
    options: VideoInitOptions = {},
) => {
    if (typeof rawSlugOrId !== 'string' && typeof rawSlugOrId !== 'number') {
        console.trace(
            `[video] Slug or user ID is required! rawSlugOrId: ${String(rawSlugOrId)}`,
        )
        throw new Error(
            `[video] Slug or user ID is required! rawSlugOrId:' + ${String(rawSlugOrId)}`,
        )
    }

    const id = resolveID(rawSlugOrId)

    if (!id) {
        console.error('[video] unable to resolve ID', rawSlugOrId)
        return
    }

    registerSnaps(id, SnapSize.large)
    // console.log(`[video ${id}] init`, rawSlugOrId, options)
    const state = setupDependencies(id)

    if (state.id === id) {
        // console.log(
        //     `[video ${id}] reusing cached info`,
        //     JSON.stringify(options, null, 4),
        // )
        state.initOptions = options
        //
        //
        // Todo: Look into why "initVideo" is being called more than once in SSR
        // room openings or even regularly opening a room
        //
        //
        if (options.mute === undefined && state.instance) {
            const { mute } = getVideoState()

            // console.log(
            //     `[video ${id}] setting mute state (initVideo option). Muted: ${mute}`,
            // )

            setInstanceMute(state, mute)
        }
        return state
    }

    // console.log(
    //     `[video ${id}] initializing new video instance`,
    //     JSON.stringify(options, null, 4),
    // )

    state.id = id
    state.initOptions = options
    emitInitializing(id, options.videoType)

    const { autoStarted } = options
    const callback = () => {
        const state = VIDEOSTATE.videos[id]
        if (!state) {
            console.trace(
                `[video ${id}] state is undefined in the config callback`,
            )
            return
        }

        cancelAnimationFrame(state.timers.retryUser)
        cancelAnimationFrame(state.timers.retrySession)
        cancelAnimationFrame(state.timers.retryVideoPlayer)

        const Player = getVideoPlayerClass()
        if (!Player) {
            if (!state.timers.retryVideoPlayer) {
                console.warn(`[video ${id}] VideoPlayer not available`)
                videoPlayerReady().catch(() => {
                    // The round of import attempts failed. Stop polling frames
                    // and clear the gate so the next callback run starts
                    // another round instead of polling forever for a load
                    // that ended. That run comes from the model's next state
                    // update, or from a rebuilt video state after teardown; a
                    // repeated initVideo for the same room returns early.
                    cancelAnimationFrame(state.timers.retryVideoPlayer)
                    state.timers.retryVideoPlayer = null
                })
            }
            state.timers.retryVideoPlayer = requestAnimationFrame(callback)
            return
        }

        if (
            !USERSSTATE.users[slugToID['me']]?.session ||
            !USERSSTATE.users[slugToID['me']]?.auth
        ) {
            if (!state.timers.retrySession) {
                console.warn(`[video ${id}] client session not available`)
            }
            state.timers.retrySession = requestAnimationFrame(callback)
            return
        }

        if (!USERSSTATE.users[id]) {
            if (!state.timers.retryUser) {
                console.warn(`[video ${id}] user not found in state`)
            }
            state.timers.retryUser = requestAnimationFrame(callback)
            return
        }

        state.timers.retryUser = null
        state.timers.retrySession = null
        state.timers.retryVideoPlayer = null

        const config = generateVideoConfig(id)
        if (!config) {
            console.error(
                `[video ${id}] user not found in state.` +
                    'This should never happen, if it does, please investigate.',
            )
            return
        }

        const { sessionId: previousSessionId, ...previousConfig } =
            state.config || {}

        const { sessionId: incomingSessionId, ...incomingConfig } = config

        const configEqual = isEqualWith(
            previousConfig,
            incomingConfig,
            areFunctionsEqual,
        )

        // An offline model has no video server or flags, but the player is
        // still needed for the SDK's offline video avatars; it builds no
        // stream URL until the model is live, and the config change that
        // brings the server rebuilds it.
        const offline = config.modelState === FCVIDEO_OFFLINE
        if (
            configEqual ||
            (!offline && (!config.videoDomain || !config.modelFlags))
        ) {
            // console.log(`[video ${id}] no relevant config update found!`)
            return
        }

        const resolveInstance = () => {
            if (
                showsAgeGatedPoster(
                    SECURESTATE.ageVerification.ageGateActive,
                    SECURESTATE.displayConfig,
                )
            )
                return
            if (INSTANCE_COUNTER[id] === undefined) {
                INSTANCE_COUNTER[id] = 0
            } else {
                INSTANCE_COUNTER[id]++
            }

            const { state: modelState } = config
            const transitionID =
                'transitioning_' + INSTANCE_COUNTER[id] + '_' + id

            const sameShowType =
                !!state?.instance && state.cache.state === modelState
            if (sameShowType && replacesInPlace(state)) {
                state.instance.Disconnect()
                state.instance = null
            } else if (sameShowType) {
                // console.log(`[video ${id}] transitioning`)

                state.instance.changeId(transitionID)

                VIDEOSTATE.videos[transitionID] = state

                delete VIDEOSTATE.videos[id]

                state.container.id = 'transitioning_' + state.container.id

                const newState = setupDependencies(id)

                const {
                    placeholders,
                    unsubscribe,
                    container: __,
                    instance: _,
                    // Bound to the old container, which keeps its listeners
                    // until torn down; the replacement gets its own below.
                    removeTouch: _removeTouch,
                    ...other
                } = state

                VIDEOSTATE.videos[id] = merge(newState, other)

                // console.log(
                //     `[video ${id}] timers:`,
                //     JSON.stringify(VIDEOSTATE.videos[id].timers, null, 4),
                // )

                VIDEOSTATE.videos[id].placeholders = placeholders
                VIDEOSTATE.videos[id].unsubscribe = unsubscribe

                state.unsubscribe = null

                VIDEOSTATE.videos[id].cache.options = cloneDeep(
                    state.cache.options,
                )

                // the layouts never see the container change, see
                // attachTouchListeners
                VIDEOSTATE.videos[id].containerClasses = state.containerClasses
                VIDEOSTATE.videos[id].touchListeners = state.touchListeners
                applyContainerClasses(VIDEOSTATE.videos[id])
                attachTouchListeners(VIDEOSTATE.videos[id])
            }

            if (state.cache.state !== modelState) {
                state.cache.state = modelState

                if (state.instance) {
                    state.instance.Disconnect()
                    state.instance = null
                }
            }

            const newState = VIDEOSTATE.videos[id]
            const { volume, mute } = getVideoState()
            // Controls, zoom and portrait theater come from the rendering
            // placeholder: a show-state rebuild keeps this state object and
            // its cache.options identity, so the animation frame never
            // re-applies options to the new instance. Use the initial control
            // options only before a placeholder has registered.
            const topOptions =
                newState.placeholders[newState.placeholders.length - 1]?.options
            const { onlyVolumeControls = false, allowControls = true } =
                topOptions ?? options
            const disableZoom = !!topOptions?.disableZoom
            const portraitTheater = !!topOptions?.portraitTheater
            newState.portraitTheater = portraitTheater

            newState.volume = volume

            const videoPlayerConfig: VideoConfig = {
                ...config,
                // a rebuild keeps the mute the player last settled on (see
                // setInstanceMute); the init options and the global mute
                // only start a player that has none
                mute: newState.hostMuted ?? options.mute ?? mute,
                containerEl: newState.container,
                cacheModelVolume: false,
                defaultVolume: volume,
                onlyVolumeControls,
                allowControls,
                // Room players only, not generateVideoConfig: snap players are
                // torn down while a model is offline, and preload would fetch
                // a clip list per grid tile.
                videoAvatars: VIDEO_AVATARS_OPTIONS,
                // portrait theater enters after render() and must start
                // from the base config, see applyZoomOptions
                zoom: disableZoom && !portraitTheater ? false : config.zoom,
                // the SDK creates its chat-overlay and theater-mode buttons
                // at render() once these are set; kept out of `config` so the
                // change detection above never sees them
                ...videoViewModeHandlers(id),
            }

            // console.log(
            //     `[video ${id}] configuration${!state.config ? '' : ' changed'}`,
            //     videoPlayerConfig,
            //     // JSON.stringify(videoPlayerConfig, null, 4),
            // )

            const instance = new Player(videoPlayerConfig)

            newState.config = config
            newState.instance = instance
            newState.cache.disableZoom = disableZoom
            newState.cache.portraitTheater = portraitTheater
            newState.playing = false
            newState.muted = videoPlayerConfig.mute
            // the flag waits for the volumechange of an auto-mute; the new
            // player has none pending (the SDK suppresses it behind a
            // placard), so a kept flag would swallow the viewer's next change
            newState.wasAutoMuted = false

            // The SDK's playback snapshot is the source of truth for
            // `playing`: true from the media `playing` event, false on pause,
            // waiting, ended, loadstart, a restart, a non-loading placard and
            // destroy. It commits before the native event is forwarded, so the
            // onPlaying callback below already sees it true.
            instance.on('playbackstatechange', (_event, playback) => {
                newState.playing = playback.playing
                setVideoAvatar(newState, id, playback.videoAvatar)
            })

            // console.log(`[video ${id}] created instance`)

            instance.setOnPlayingCallback(() => {
                if (newState !== state) {
                    destroyTransitionInstance(state, transitionID)
                }

                newState.cache.playing = newState.playing
                emitPlaying(id, newState.playing)
                const placeholder =
                    newState.placeholders[newState.placeholders.length - 1]
                if (placeholder && placeholder.onPlaying) {
                    placeholder.onPlaying(newState.playing)
                }
            })

            instance.onVolumeChange((currentVolume, muted) => {
                newState.muted = muted

                if (
                    newState.cache.hidden ||
                    newState.timers.hide ||
                    newState.wasAutoMuted ||
                    newState.playing === false
                ) {
                    if (newState.wasAutoMuted) {
                        newState.wasAutoMuted = false
                    }
                    return
                }

                // the viewer's change, or the app's on a visible player
                newState.hostMuted = muted
                // theirs to keep once a clip is done with the sound
                mutedForClip.delete(newState.id)

                // console.log(`[video ${id}] attempting to remove autoMutedBy`)

                Object.keys(newState.autoMutedBy).forEach((otherID) => {
                    const otherState = VIDEOSTATE.videos[otherID]

                    delete otherState.autoMuted[newState.id]
                    delete newState.autoMutedBy[otherID]

                    // console.log(
                    //     `[video ${id}] removed autoMutedBy`,
                    //     JSON.stringify(otherState.autoMuted, null, 4),
                    //     JSON.stringify(newState.autoMutedBy, null, 4),
                    // )
                })

                const { volume } = newState

                if (typeof volume === 'number') {
                    newState.volume = null
                    instance.setVolume(volume)
                }

                const { initOptions: options } = newState

                if (typeof options.onMute === 'function') {
                    options.onMute(muted)
                } else if (options.mute === undefined) {
                    setVideoMuteState(muted)
                }

                if (!muted) {
                    setVideoVolumeState(currentVolume)
                }
            })

            instance.render()
            // Every rebuild replaces <video>, even when its container and
            // placeholder options stay the same. Bind now because a hidden
            // tab may not run the frame that normally applies those options.
            newState.onclick = undefined
            setOnclick(topOptions?.onclick, id)
            if (portraitTheater) instance.setPortraitTheaterMode(true)
            applyViewModeButtons(instance, topOptions ?? {}, portraitTheater)
            emitInstance(id)
        }

        clearTimeout(state.timers.callback)

        //
        //
        // Marc: If the state of the model's stream has changed, we should
        // destroy and initiate the next stream right away
        //
        //
        if (
            (!autoStarted && state.cache.state !== config.state) ||
            (state?.instance && !state.playing)
        ) {
            // console.log(
            //     `[video ${id}] running immidiate config update`,
            //     state.cache.state,
            //     config.state,
            // )
            resolveInstance()
            return
        }

        //
        //
        // Marc: Adding some debounce here to avoid quick configuration changes
        // from unnecessarily destroying and initializing videos
        //
        //
        state.timers.callback = setTimeout(() => {
            resolveInstance()
        }, 1000)
    }
    state.unsubscribe = pubSubUsers.on(UserEvent.UPDATE + '::' + id, callback)

    callback()
    return state
}

const setVideoState = (updates) => {
    VIDEOSTATE.video = { ...VIDEOSTATE.video, ...updates }
}

export const setVideoVolumeState = (volume: number) => {
    setVideoState({ volume })
}

export const setVideoMuteState = (mute: boolean) => {
    setVideoState({ mute })
}
